import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Check } from 'lucide-react';

/**
 * A filled disc with a check inside it — the "this is settled" mark.
 *
 * WHY IT EXISTS. Lucide's `CircleCheck` is an OUTLINE: a ring with a check drawn in
 * the same stroke. The design's mark is the opposite — a solid disc with the check
 * knocked out of it in white — and there is no lucide glyph for that, so every
 * surface that needed one built the same Box-plus-Check by hand. Three of them are
 * in the tree already (`CheckRounded` in the audit-review congratulations step,
 * `StyledCompleteIcon` on the audit item, and the finalised-audits card), at three
 * sizes and two greens.
 *
 * Solid only, because that is all the design draws: a filled disc marking a finished
 * thing. A pale-fill variant would be an API nobody has asked for.
 */

const Disc = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size: number }>(({ theme, size }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: size,
  height: size,
  padding: 2,
  borderRadius: '50%',
  backgroundColor: theme.palette.success.main,
  color: theme.palette.common.white,
}));

export interface CheckBadgeProps {
  /** Diameter in px. The check scales with it. */
  size?: number;
  className?: string;
  'data-testid'?: string;
}

export function CheckBadge({ size = 16, className, ...rest }: CheckBadgeProps) {
  return (
    <Disc size={size} className={className} data-testid={rest['data-testid']}>
      {/* Ratio, not a fixed size: the check has to stay inside the disc at every
          diameter the callers use (16 in a list, 40 on a card). Heavier stroke than
          lucide's default, because it is reversed out of a saturated fill. */}
      <Check size={Math.round(size * 0.66)} strokeWidth={3} aria-hidden />
    </Disc>
  );
}

export default CheckBadge;
